import { FC, useEffect, useState } from 'react';
import styled from '@emotion/styled';

import AddAvailList from './AddAvailList';
import RemoveAvailList from './RemoveAvailList';
import { ScheduleAvailType } from '@/redux/slices/slice_types';

type RightsSelectionSectionProps = {
  tab: ScheduleAvailType;
  selectedRightsList: string[];
  rightsOptions?: string[];
  onChangeSelectedRights: (next: string[]) => void;
  scrollToElementName?: string;
  onlyRights?: boolean;
};

const RightsSelectionSection: FC<RightsSelectionSectionProps> = ({
  tab,
  selectedRightsList,
  rightsOptions,
  onChangeSelectedRights,
  scrollToElementName,
  onlyRights = false
}) => {
  const [draftSelectedRights, setDraftSelectedRights] = useState<string[]>(selectedRightsList ?? []);

  const handleAddRight = (right: string) => {
    if (draftSelectedRights.includes(right)) return;

    const nextSelectedRights = [...draftSelectedRights, right];
    setDraftSelectedRights(nextSelectedRights);
    onChangeSelectedRights(nextSelectedRights);
  };

  const handleAddMultipleRights = (rights: string[]) => {
    const rightsToAdd = rights.filter((right) => !draftSelectedRights.includes(right));
    if (rightsToAdd.length === 0) return;

    const nextSelectedRights = [...draftSelectedRights, ...rightsToAdd];
    setDraftSelectedRights(nextSelectedRights);
    onChangeSelectedRights(nextSelectedRights);
  };

  const handleRemoveRight = (right: string) => {
    if (!draftSelectedRights.includes(right)) return;

    const nextSelectedRights = draftSelectedRights.filter((selectedRight) => selectedRight !== right);
    setDraftSelectedRights(nextSelectedRights);
    onChangeSelectedRights(nextSelectedRights);
  };

  const handleRemoveMultipleRights = (rights: string[]) => {
    if (rights.length === 0) return;

    const nextSelectedRights = draftSelectedRights.filter((selectedRight) => !rights.includes(selectedRight));
    if (nextSelectedRights.length === draftSelectedRights.length) return;

    setDraftSelectedRights(nextSelectedRights);
    onChangeSelectedRights(nextSelectedRights);
  };

  useEffect(() => {
    setDraftSelectedRights(selectedRightsList ?? []);
  }, [selectedRightsList, tab]);

  useEffect(() => {
    if (onlyRights && rightsOptions?.length) {
      handleAddMultipleRights(rightsOptions);
    }
  }, [onlyRights, rightsOptions]);

  return (
    <StyledContent>
      <StyledColumns>
        <AddAvailList
          onItemAdd={handleAddRight}
          onItemsAdd={handleAddMultipleRights}
          type={tab}
          selectedRightsList={draftSelectedRights}
          rightsOptions={rightsOptions}
          scrollToElementName={scrollToElementName}
          onlyRights={onlyRights}
        />
        <RemoveAvailList
          onItemRemove={handleRemoveRight}
          onItemsRemove={handleRemoveMultipleRights}
          rightsList={draftSelectedRights}
          type={tab}
          rightsOptions={rightsOptions}
        />
      </StyledColumns>
    </StyledContent>
  );
};

export default RightsSelectionSection;

const StyledColumns = styled.div`
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing(2)};

  ${({ theme }) => theme.breakpoints.down('md')} {
    grid-template-columns: 1fr;
  }
`;

const StyledContent = styled.div`
  flex: 1;
  min-height: 535px;
  max-height: 535px;
  display: flex;
  flex-direction: column;
`;
